
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Wine, WineType, CellarStats, ConsumedWine } from './types';
import CellarView from './components/CellarView';
import AddWineModal from './components/AddWineModal';
import SommelierTool from './components/SommelierTool';
import Dashboard from './components/Dashboard';
import ProfileSelector from './components/ProfileSelector';
import ApiKeyGuard from './components/ApiKeyGuard';
import { syncToDrive, authenticate, fetchFromDrive } from './services/googleDriveService';
import { saveWinesLocally, loadWinesLocally } from './services/storageService';

const App: React.FC = () => {
  const [profile, setProfile] = useState<string | null>(() => {
    try {
      const p = localStorage.getItem('divino_current_profile');
      return (p && p.trim() !== "") ? p : null;
    } catch { return null; }
  });
  
  const [wines, setWines] = useState<Wine[]>([]);
  const [history, setHistory] = useState<ConsumedWine[]>([]);
  const [activeTab, setActiveTab] = useState<'cellar' | 'stats' | 'sommelier'>('cellar');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailView, setIsDetailView] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'done' | 'error'>('idle');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const isSyncingFromCloud = useRef<boolean>(false);

  // Caricamento dati iniziale
  useEffect(() => {
    const initData = async () => {
      if (!profile) {
        setIsInitialLoad(false);
        return;
      }
      try {
        isSyncingFromCloud.current = true;
        const rawData = await loadWinesLocally(profile);
        
        if (Array.isArray(rawData)) {
          setWines(rawData);
          const savedHistory = localStorage.getItem(`divino_history_${profile}`);
          if (savedHistory) setHistory(JSON.parse(savedHistory));
        } else if (rawData && typeof rawData === 'object') {
          const data = rawData as any;
          setWines(Array.isArray(data.wines) ? data.wines : []);
          setHistory(Array.isArray(data.history) ? data.history : []);
        }

        // Se è un profilo cloud e abbiamo un token, proviamo a vedere se c'è roba più fresca
        if (profile === "Cloud_Backup" && window.gapi?.client?.getToken()) {
          setSyncStatus('syncing');
          const cloudData = await fetchFromDrive(profile);
          if (cloudData) {
            setWines(Array.isArray(cloudData.wines) ? cloudData.wines : []);
            setHistory(Array.isArray(cloudData.history) ? cloudData.history : []);
          }
          setSyncStatus('idle');
        }
      } catch (e) {
        console.error("Errore caricamento iniziale:", e);
      } finally {
        setIsInitialLoad(false);
        setTimeout(() => { isSyncingFromCloud.current = false; }, 500);
      }
    };
    initData();
  }, [profile]);

  // Sincronizzazione Automatica e Manuale
  const performSync = useCallback(async (currentWines: Wine[], currentHistory: ConsumedWine[], isForced = false) => {
    if (!profile || isInitialLoad) return;
    try {
      const dataToSave = { wines: currentWines, history: currentHistory };
      
      // Salvataggio locale sempre prioritario
      await saveWinesLocally(profile, dataToSave as any);
      localStorage.setItem(`divino_history_${profile}`, JSON.stringify(currentHistory));
      
      const isGoogleProfile = profile === "Cloud_Backup"; 
      if (isGoogleProfile) {
        setSyncStatus('syncing');
        
        // Se forzato o manca il token, ri-autentichiamo
        if (isForced || !window.gapi?.client?.getToken()) {
          await authenticate();
        }

        const success = await syncToDrive(profile, dataToSave as any);
        setSyncStatus(success ? 'done' : 'error');
        if (success) setTimeout(() => setSyncStatus('idle'), 3000);
        else setTimeout(() => setSyncStatus('idle'), 5000);
      }
    } catch (e) {
      console.error("Errore Sync:", e);
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 5000);
    }
  }, [profile, isInitialLoad]);

  // Sync automatico su cambiamenti
  useEffect(() => {
    if (!profile || isInitialLoad || isSyncingFromCloud.current) return;
    const timer = setTimeout(() => performSync(wines, history), 3000);
    return () => clearTimeout(timer);
  }, [wines, history, profile, isInitialLoad, performSync]);

  const handleManualSync = () => {
    if (syncStatus === 'syncing') return;
    performSync(wines, history, true);
  };

  const stats = useMemo((): CellarStats => {
    const s = { totalBottles: 0, totalValue: 0, byType: {} };
    if (!Array.isArray(wines)) return s;
    wines.forEach(w => {
      if (!w) return;
      s.totalBottles += (Number(w.quantity) || 0);
      s.totalValue += (Number(w.price || 0) * Number(w.quantity || 0));
      const t = w.type || WineType.OTHER;
      s.byType[t] = (s.byType[t] || 0) + (Number(w.quantity) || 0);
    });
    return s;
  }, [wines]);

  const handleUpdateQty = (id: string, delta: number) => {
    setWines(prev => {
      const wine = prev.find(w => w.id === id);
      if (wine && delta === -1) {
        const newEntry: ConsumedWine = {
          id: Math.random().toString(36).substr(2, 9),
          name: wine.name,
          producer: wine.producer,
          vintage: wine.vintage,
          type: wine.type,
          consumedAt: Date.now(),
          imageUrl: wine.imageUrls[0]
        };
        setHistory(h => [newEntry, ...h].slice(0, 50));
      }
      return prev.map(w => w.id === id ? { ...w, quantity: Math.max(0, w.quantity + delta) } : w).filter(w => w.quantity > 0);
    });
  };

  const handleSelectProfile = (p: string) => {
    localStorage.setItem('divino_current_profile', p);
    setProfile(p);
  };

  const handleLogout = () => {
    if (confirm("Vuoi uscire? I dati locali rimarranno salvati.")) {
      setProfile(null);
      localStorage.removeItem('divino_current_profile');
    }
  };

  if (isInitialLoad) {
    return (
      <div className="fixed inset-0 bg-[#0a0503] flex items-center justify-center">
        <div className="w-12 h-12 border-3 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <ApiKeyGuard>
      {!profile ? (
        <ProfileSelector onSelect={handleSelectProfile} />
      ) : (
        <div className="h-full w-full bg-[#0a0503] flex flex-col overflow-hidden relative">
          
          {!isDetailView && (
            <header className="flex-none px-8 pt-[calc(env(safe-area-inset-top)+0.5rem)] pb-4 flex flex-col items-center bg-[#0a0503] border-b border-white/5 z-50">
              <button onClick={handleLogout} className="absolute left-6 top-[calc(env(safe-area-inset-top)+1rem)] p-2 bg-white/5 rounded-xl text-slate-500 active:scale-75">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13.8 12H3"/></svg>
              </button>
              
              <h1 className="text-3xl font-bold text-amber-500 serif tracking-tight">Divino</h1>
              
              <button 
                onClick={handleManualSync}
                className="flex items-center gap-2 px-3 py-1 mt-1 rounded-full bg-white/5 active:bg-white/10 transition-all border border-transparent hover:border-amber-500/20"
              >
                <span className={`text-[8px] font-black uppercase tracking-[0.3em] ${syncStatus === 'error' ? 'text-red-500' : 'text-amber-600/60'}`}>
                  {syncStatus === 'syncing' ? 'Sincronizzazione...' : 
                   syncStatus === 'done' ? 'Backup OK' : 
                   syncStatus === 'error' ? 'Errore Sync' : profile}
                </span>
                
                {profile === "Cloud_Backup" && (
                  <div className={`w-2 h-2 rounded-full transition-all ${
                    syncStatus === 'syncing' ? 'bg-blue-500 animate-pulse' : 
                    syncStatus === 'done' ? 'bg-green-500' : 
                    syncStatus === 'error' ? 'bg-red-500' : 'bg-amber-500/20'
                  }`}></div>
                )}
              </button>
            </header>
          )}

          <main className="flex-1 w-full relative overflow-y-auto ios-scroll-container no-scrollbar">
            <div className={`max-w-4xl mx-auto w-full flex flex-col ${isDetailView ? '' : 'px-4 py-6'}`}>
              {activeTab === 'cellar' && (
                <CellarView 
                  wines={wines} 
                  onRemove={(id) => setWines(prev => prev.filter(w => w.id !== id))} 
                  onUpdateQty={handleUpdateQty} 
                  onUpdateWine={(id, updates) => setWines(prev => prev.map(w => w.id === id ? { ...w, ...updates } : w))} 
                  onViewToggle={setIsDetailView}
                />
              )}
              {!isDetailView && activeTab === 'stats' && <Dashboard stats={stats} wines={wines} history={history} onRemoveHistory={(hid) => setHistory(prev => prev.filter(h => h.id !== hid))} />}
              {!isDetailView && activeTab === 'sommelier' && <SommelierTool wines={wines} />}
              
              {!isDetailView && <div className="h-40 flex-none w-full" />}
            </div>
          </main>

          {!isDetailView && (
            <>
              {/* PLUS BUTTON */}
              <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] right-6 z-[60]">
                <button 
                  onClick={() => setIsModalOpen(true)} 
                  className="bg-amber-600 text-white w-14 h-14 rounded-full shadow-2xl flex items-center justify-center active:scale-90 border-4 border-[#0a0503]"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                </button>
              </div>

              {/* NAV BAR */}
              <nav className="fixed bottom-0 left-0 right-0 bg-[#0a0503]/95 backdrop-blur-2xl border-t border-white/5 z-[70] flex flex-col pt-3 pb-[env(safe-area-inset-bottom)]">
                <div className="flex justify-around items-center h-16 px-6">
                  <NavBtn active={activeTab === 'cellar'} onClick={() => setActiveTab('cellar')} label="Cantina" icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>} />
                  <NavBtn active={activeTab === 'stats'} onClick={() => setActiveTab('stats')} label="Stat" icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>} />
                  <NavBtn active={activeTab === 'sommelier'} onClick={() => setActiveTab('sommelier')} label="Sommelier" icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>} />
                </div>
              </nav>
            </>
          )}

          {isModalOpen && <AddWineModal onAdd={(newW) => {setWines(prev => [...prev, ...newW]); setIsModalOpen(false);}} onClose={() => setIsModalOpen(false)} />}
        </div>
      )}
    </ApiKeyGuard>
  );
};

const NavBtn: React.FC<{ active: boolean, onClick: () => void, label: string, icon: React.ReactNode }> = ({ active, onClick, label, icon }) => (
  <button onClick={onClick} className={`flex flex-col items-center justify-center transition-all px-4 ${active ? 'text-amber-500 scale-105' : 'text-slate-600'}`}>
    {icon}
    <span className="text-[7px] mt-1.5 font-black uppercase tracking-[0.2em]">{label}</span>
  </button>
);

export default App;
